import { useEffect } from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { useOnceReveal } from '@common/hooks';
import { PdpProductSizeType } from '@domains/pdp-common';

import { useLegacySizeRecommendationService } from '../services/useLegacySizeRecommendationService';
import { useLegacySizeRecommendationUBL } from '../services/useLegacySizeRecommendationUBL';
import { LegacyOthersSizeContainer } from './LegacyOthersSizeContainer';
import { LegacySimilarSizeContainer } from './LegacySimilarSizeContainer';
import { LegacyUnregisteredSizeContainer } from './LegacyUnregisteredSizeContainer';

export interface LegacySizeRecommendationSectionContainerProps {
  onSizeRecommendRefChange?: (node: HTMLDivElement) => void;
  disabledGutter?: boolean;
}

export const LegacySizeRecommendationSectionContainer: React.FC<
  React.PropsWithChildren<LegacySizeRecommendationSectionContainerProps>
> = ({ onSizeRecommendRefChange, disabledGutter }) => {
  const { sizeRecommendationImpression } = useLegacySizeRecommendationUBL();
  const { targetRef, isRevealed } = useOnceReveal();

  const { catalogProductId, visible, sizeRecommendation, recommendationType, userAccount } =
    useLegacySizeRecommendationService();

  useEffect(() => {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    Object.keys(sessionStorage).forEach((key) => {
      if (
        !key.includes(catalogProductId) &&
        (key.includes('size_recommend_login') || key.includes('size_recommend_body_info'))
      ) {
        sessionStorage.removeItem(key);
      }
    });

    const is_loggedin = sessionStorage.getItem(`size_recommend_login?${catalogProductId}`);
    if (visible && isRevealed) {
      const { recommendationType, recommendOptionValueList, userAccount } = sizeRecommendation;
      if (is_loggedin && !userAccount?.body) {
        sessionStorage.removeItem(`size_recommend_login?${catalogProductId}`);
        return;
      }
      sizeRecommendationImpression({
        catalogProductId,
        recommendationType,
        recommendOptionValueList: recommendOptionValueList.map(({ ranking, name }) => ({ ranking, name })),
      });
      sessionStorage.removeItem(`size_recommend_login?${catalogProductId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isRevealed]);

  if (visible) {
    if (recommendationType === PdpProductSizeType.NONE || !userAccount || !userAccount.body) {
      return (
        <div ref={targetRef}>
          <Layout ref={onSizeRecommendRefChange} disabledGutter={disabledGutter}>
            <LegacyUnregisteredSizeContainer />
          </Layout>
        </div>
      );
    }

    if (recommendationType === PdpProductSizeType.OPTION) {
      return (
        <div ref={targetRef}>
          <Layout ref={onSizeRecommendRefChange} disabledGutter={disabledGutter}>
            <LegacyOthersSizeContainer />
          </Layout>
        </div>
      );
    }

    return (
      <div ref={targetRef}>
        <Layout ref={onSizeRecommendRefChange} disabledGutter={disabledGutter}>
          <LegacySimilarSizeContainer />
        </Layout>
      </div>
    );
  }

  return null;
};

const Layout = styled.div<{ disabledGutter?: boolean }>`
  width: 100%;
  height: auto;
  ${({ disabledGutter }) => !disabledGutter && `border-top: 8px solid ${vars.v3Color.gray100};`}
`;
